import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUtensils, faMagnifyingGlass, faXmark, faPlus, faMinus,
  faCartShopping, faChevronLeft, faCircleCheck, faSpinner,
  faReceipt, faTrash,
} from "@fortawesome/free-solid-svg-icons";
import "./NewOrderFlow.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface MenuItem {
  id: number;
  name: string;
  category: string;
  price: number;
  description?: string;
  available: boolean;
  image_url?: string;
  unit?: string;
  available_from?: string;
  available_to?: string;
}

interface CartItem {
  menuItem: MenuItem;
  qty: number;
  size: "S" | "M" | "L";
  notes: string;
}

type Step = "browse" | "review" | "success";

const SIZES: { key: "S" | "M" | "L"; label: string; sub: string; mult: number }[] = [
  { key: "S", label: "S",  sub: "Small",   mult: 0.85 },
  { key: "M", label: "M",  sub: "Regular", mult: 1 },
  { key: "L", label: "L",  sub: "Large",   mult: 1.2 },
];

const CATEGORY_ICONS: Record<string, string> = {
  "Breakfast": "🌅",
  "Main":      "🍽",
  "Snacks":    "🥨",
  "Drinks":    "🥤",
  "Desserts":  "🍰",
  "Other":     "📦",
};

function isAvailableNow(item: MenuItem): boolean {
  if (!item.available_from || !item.available_to) return item.available;
  if (!item.available) return false;
  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const [fh, fm] = item.available_from.split(":").map(Number);
  const [th, tm] = item.available_to.split(":").map(Number);
  const fromMins = fh * 60 + fm;
  const toMins   = th * 60 + tm;
  return fromMins <= toMins
    ? nowMins >= fromMins && nowMins <= toMins
    : nowMins >= fromMins || nowMins <= toMins;
}

function sizePrice(base: number, mult: number) {
  return Math.round(base * mult * 100) / 100;
}

interface Props {
  onOrderPlaced: () => void;
  onViewOrders: () => void;
}

export default function NewOrderFlow({ onOrderPlaced, onViewOrders }: Props) {
  const [step, setStep]       = useState<Step>("browse");
  const [menu, setMenu]       = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat]         = useState("All");
  const [search, setSearch]   = useState("");
  const [cart, setCart]       = useState<CartItem[]>([]);

  // Config modal
  const [configItem, setConfigItem] = useState<MenuItem | null>(null);
  const [cfgSize,   setCfgSize]   = useState<"S" | "M" | "L">("M");
  const [cfgQty,    setCfgQty]    = useState(1);
  const [cfgNotes,  setCfgNotes]  = useState("");

  // Order details
  const [roomNumber, setRoomNumber] = useState("");
  const [guestName,  setGuestName]  = useState("");
  const [orderNotes, setOrderNotes] = useState("");
  const [placing,    setPlacing]    = useState(false);
  const [placeError, setPlaceError] = useState("");
  const [lastOrderId, setLastOrderId] = useState<number | null>(null);
  // Settlement
  const [rsPayMethod,     setRsPayMethod]     = useState<"cash" | "card" | "instapay">("cash");
  const [rsCardNumber,    setRsCardNumber]    = useState("");
  const [rsTransactionRef, setRsTransactionRef] = useState("");

  const loadMenu = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/roomservice/menu`);
      setMenu(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadMenu(); }, [loadMenu]);

  // All categories
  const categories = ["All", ...Array.from(new Set(menu.map(m => m.category))).sort()];

  // Filtered menu
  const filtered = menu.filter(m => {
    if (cat !== "All" && m.category !== cat) return false;
    if (search) {
      const q = search.toLowerCase();
      return m.name.toLowerCase().includes(q) || (m.description ?? "").toLowerCase().includes(q);
    }
    return true;
  });

  // Cart helpers
  const cartTotal = cart.reduce((s, ci) => s + sizePrice(ci.menuItem.price, SIZES.find(sz => sz.key === ci.size)!.mult) * ci.qty, 0);
  const cartCount = cart.reduce((s, ci) => s + ci.qty, 0);

  const openConfig = (item: MenuItem) => {
    if (!isAvailableNow(item)) return;
    setConfigItem(item);
    setCfgSize("M");
    setCfgQty(1);
    setCfgNotes("");
  };

  const addToCart = () => {
    if (!configItem) return;
    setCart(prev => {
      const existing = prev.findIndex(ci => ci.menuItem.id === configItem.id && ci.size === cfgSize && ci.notes === cfgNotes);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { ...updated[existing], qty: updated[existing].qty + cfgQty };
        return updated;
      }
      return [...prev, { menuItem: configItem, qty: cfgQty, size: cfgSize, notes: cfgNotes }];
    });
    setConfigItem(null);
  };

  const removeFromCart = (idx: number) => {
    setCart(prev => prev.filter((_, i) => i !== idx));
  };

  const updateCartQty = (idx: number, delta: number) => {
    setCart(prev => {
      const updated = [...prev];
      const newQty = updated[idx].qty + delta;
      if (newQty <= 0) return updated.filter((_, i) => i !== idx);
      updated[idx] = { ...updated[idx], qty: newQty };
      return updated;
    });
  };

  const placeOrder = async () => {
    if (!roomNumber.trim()) { setPlaceError("Room number is required"); return; }
    if (cart.length === 0)  { setPlaceError("Cart is empty"); return; }
    setPlacing(true);
    setPlaceError("");
    try {
      const items = cart.map(ci => ({
        name:  ci.menuItem.name + (ci.size !== "M" ? ` (${SIZES.find(s => s.key === ci.size)?.sub})` : ""),
        qty:   ci.qty,
        price: sizePrice(ci.menuItem.price, SIZES.find(s => s.key === ci.size)!.mult),
        notes: ci.notes || undefined,
      }));
      const total = items.reduce((s, i) => s + i.price * i.qty, 0);
      const res = await axios.post(`${API}/api/roomservice/orders`, {
        room_number:     roomNumber.trim(),
        guest_name:      guestName.trim() || undefined,
        notes:           orderNotes.trim() || undefined,
        items,
        total,
        payment_method:  rsPayMethod,
        card_number:     rsPayMethod === "card"     ? rsCardNumber.trim()     || undefined : undefined,
        transaction_ref: rsPayMethod === "instapay" ? rsTransactionRef.trim() || undefined : undefined,
      });
      setLastOrderId(res.data?.id ?? null);
      setStep("success");
      onOrderPlaced();
    } catch (e: any) {
      setPlaceError(e?.response?.data?.error ?? e?.message ?? "Failed to place order");
    } finally {
      setPlacing(false);
    }
  };

  const resetFlow = () => {
    setStep("browse");
    setCart([]);
    setRoomNumber("");
    setGuestName("");
    setOrderNotes("");
    setPlaceError("");
    setLastOrderId(null);
    setRsPayMethod("cash");
    setRsCardNumber("");
    setRsTransactionRef("");
  };

  // ── BROWSE STEP ──────────────────────────────────────────────
  if (step === "browse") return (
    <div className="nof-root">
      {/* Top bar */}
      <div className="nof-topbar">
        <div className="nof-topbar-title">
          <FontAwesomeIcon icon={faUtensils} />
          New Order
        </div>
        <div className="nof-guest-inputs">
          <input
            className="nof-input"
            placeholder="Room number *"
            value={roomNumber}
            onChange={e => setRoomNumber(e.target.value)}
          />
          <input
            className="nof-input"
            placeholder="Guest name"
            value={guestName}
            onChange={e => setGuestName(e.target.value)}
          />
        </div>
      </div>

      {/* Category tabs */}
      <div className="nof-cats">
        {categories.map(c => (
          <button
            key={c}
            className={`nof-cat-btn${cat === c ? " active" : ""}`}
            onClick={() => setCat(c)}
          >
            {c !== "All" ? `${CATEGORY_ICONS[c] ?? ""}  ` : ""}{c}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="nof-search-wrap">
        <div className="nof-search">
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <input
            placeholder="Search menu…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <FontAwesomeIcon
              icon={faXmark}
              style={{ cursor: "pointer" }}
              onClick={() => setSearch("")}
            />
          )}
        </div>
        <span className="nof-showing">{filtered.length} item{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Grid */}
      <div className="nof-grid-wrap">
        {loading ? (
          <div style={{ textAlign: "center", padding: 60, color: "rgba(242,235,217,0.45)" }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 28 }} />
          </div>
        ) : (
          <div className="nof-grid">
            {filtered.map(item => {
              const avail = isAvailableNow(item);
              return (
                <div
                  key={item.id}
                  className={`nof-card${!avail ? " unavailable" : ""}`}
                  onClick={() => avail && openConfig(item)}
                >
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.name} className="nof-card-img" />
                  ) : (
                    <div className="nof-card-img-placeholder">
                      {CATEGORY_ICONS[item.category] ?? "🍽"}
                    </div>
                  )}
                  <div className="nof-card-body">
                    <div className="nof-card-name">{item.name}</div>
                    {item.description && (
                      <div className="nof-card-desc">{item.description}</div>
                    )}
                    <div className="nof-card-footer">
                      <div>
                        {item.price > 0 && <div className="nof-card-price">EGP {item.price}</div>}
                        {item.price > 0 && item.unit && <div className="nof-card-unit">per {item.unit}</div>}
                      </div>
                      {avail ? (
                        <div className="nof-card-add">+</div>
                      ) : (
                        <span className="nof-card-unavail-badge">Unavailable</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Cart bar */}
      {cartCount > 0 && (
        <div className="nof-cart-bar" onClick={() => setStep("review")}>
          <div className="nof-cart-left">
            <span className="nof-cart-count">{cartCount}</span>
            <span className="nof-cart-label">Review Order</span>
          </div>
          {cartTotal > 0 && <span className="nof-cart-total">EGP {cartTotal.toFixed(2)}</span>}
        </div>
      )}

      {/* Item config modal */}
      {configItem && (
        <div className="nof-item-overlay" onClick={() => setConfigItem(null)}>
          <div className="nof-item-modal" onClick={e => e.stopPropagation()}>
            {configItem.image_url && (
              <img src={configItem.image_url} alt={configItem.name} className="nof-item-img" />
            )}

            <div className="nof-item-header">
              <div className="nof-item-name">{configItem.name}</div>
              <button className="nof-item-close" onClick={() => setConfigItem(null)}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            {configItem.description && (
              <div className="nof-item-desc">{configItem.description}</div>
            )}

            {/* Size */}
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(242,235,217,0.45)", marginBottom: 8, marginTop: 0 }}>
              Size
            </p>
            <div className="nof-sizes">
              {SIZES.map(sz => (
                <button
                  key={sz.key}
                  className={`nof-size-btn${cfgSize === sz.key ? " active" : ""}`}
                  onClick={() => setCfgSize(sz.key)}
                >
                  <span className="nof-size-label">{sz.label}</span>
                  <span className="nof-size-sub">{sz.sub}</span>
                  {configItem.price > 0 && (
                    <span className="nof-size-sub" style={{ color: "#c9a84c" }}>
                      EGP {sizePrice(configItem.price, sz.mult).toFixed(0)}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Qty */}
            <div className="nof-qty-row">
              <span className="nof-qty-label">Quantity</span>
              <div className="nof-qty-ctrl">
                <button className="nof-qty-btn" onClick={() => setCfgQty(q => Math.max(1, q - 1))}>−</button>
                <span className="nof-qty-val">{cfgQty}</span>
                <button className="nof-qty-btn" onClick={() => setCfgQty(q => q + 1)}>+</button>
              </div>
            </div>

            {/* Notes */}
            <p className="nof-notes-label">Special Instructions</p>
            <textarea
              className="nof-notes-input"
              rows={2}
              placeholder="e.g. No onions, extra sauce, allergens…"
              value={cfgNotes}
              onChange={e => setCfgNotes(e.target.value)}
            />

            {/* Add to order */}
            <button className="nof-add-btn" onClick={addToCart}>
              <span>
                <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
                Add to Order
              </span>
              {configItem.price > 0 && (
                <span>EGP {(sizePrice(configItem.price, SIZES.find(s => s.key === cfgSize)!.mult) * cfgQty).toFixed(2)}</span>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ── REVIEW STEP ──────────────────────────────────────────────
  if (step === "review") return (
    <div className="nof-root">
      <div className="nof-review">
        <div className="nof-review-header">
          <button className="nof-back-btn" onClick={() => setStep("browse")}>
            <FontAwesomeIcon icon={faChevronLeft} /> Back to Menu
          </button>
          <span className="nof-review-title">
            <FontAwesomeIcon icon={faReceipt} style={{ marginRight: 8, color: "#818cf8" }} />
            Order Summary
          </span>
        </div>

        <div className="nof-receipt">
          {/* Room / Guest inputs */}
          <div className="nof-receipt-head">
            <div className="nof-receipt-inputs">
              <input
                className="nof-receipt-input"
                placeholder="Room number *"
                value={roomNumber}
                onChange={e => setRoomNumber(e.target.value)}
              />
              <input
                className="nof-receipt-input"
                placeholder="Guest name"
                value={guestName}
                onChange={e => setGuestName(e.target.value)}
              />
            </div>
            <textarea
              className="nof-receipt-notes"
              rows={2}
              placeholder="Order notes (optional)…"
              value={orderNotes}
              onChange={e => setOrderNotes(e.target.value)}
            />
          </div>

          {/* Items */}
          <div className="nof-receipt-items">
            {cart.map((ci, idx) => {
              const sizeMeta = SIZES.find(s => s.key === ci.size)!;
              const linePrice = sizePrice(ci.menuItem.price, sizeMeta.mult);
              return (
                <div key={idx} className="nof-receipt-row">
                  <div className="nof-receipt-item-info">
                    <div className="nof-receipt-item-name">
                      {ci.menuItem.name}
                      {ci.size !== "M" && <span style={{ fontWeight: 400, color: "rgba(242,235,217,0.4)" }}> · {sizeMeta.sub}</span>}
                    </div>
                    {ci.notes && (
                      <div className="nof-receipt-item-sub">"{ci.notes}"</div>
                    )}
                    <div className="nof-receipt-item-sub">EGP {linePrice.toFixed(2)} each</div>
                  </div>
                  <div className="nof-receipt-item-ctrl">
                    <button className="nof-receipt-qty-btn" onClick={() => updateCartQty(idx, -1)}>−</button>
                    <span className="nof-receipt-qty">{ci.qty}</span>
                    <button className="nof-receipt-qty-btn" onClick={() => updateCartQty(idx, 1)}>+</button>
                  </div>
                  <span className="nof-receipt-item-price">
                    EGP {(linePrice * ci.qty).toFixed(2)}
                  </span>
                  <button className="nof-receipt-remove" onClick={() => removeFromCart(idx)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Settlement */}
          <div className="nof-receipt-head" style={{ borderTop: "1px solid rgba(201,168,76,0.1)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(242,235,217,0.45)", marginBottom: 10 }}>
              Payment Method
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {(["cash", "card", "instapay"] as const).map(m => (
                <button
                  key={m}
                  onClick={() => setRsPayMethod(m)}
                  className="nof-input"
                  style={{
                    flex: 1, padding: "9px 0", borderRadius: 9, cursor: "pointer",
                    textAlign: "center", fontWeight: 700, fontSize: 12,
                    background: rsPayMethod === m ? "rgba(201,168,76,0.18)" : "rgba(255,255,255,0.03)",
                    border: rsPayMethod === m ? "1px solid rgba(201,168,76,0.5)" : "1px solid rgba(201,168,76,0.12)",
                    color: rsPayMethod === m ? "#c9a84c" : "rgba(242,235,217,0.4)",
                  }}
                >
                  {m === "cash" ? "💵 Cash" : m === "card" ? "💳 Card" : "📲 Instapay"}
                </button>
              ))}
            </div>
            {rsPayMethod === "card" && (
              <input
                className="nof-receipt-input"
                style={{ marginTop: 10, width: "100%", boxSizing: "border-box" }}
                type="text"
                inputMode="numeric"
                maxLength={4}
                placeholder="Last 3 digits on card"
                value={rsCardNumber}
                onChange={e => setRsCardNumber(e.target.value.replace(/\D/g, ""))}
              />
            )}
            {rsPayMethod === "instapay" && (
              <input
                className="nof-receipt-input"
                style={{ marginTop: 10, width: "100%", boxSizing: "border-box" }}
                type="text"
                placeholder="Transaction reference (optional)"
                value={rsTransactionRef}
                onChange={e => setRsTransactionRef(e.target.value)}
              />
            )}
          </div>

          {/* Total + Place */}
          <div className="nof-receipt-footer">
            <div className="nof-total-row">
              <span className="nof-total-label">Total</span>
              <span className="nof-total-val">EGP {cartTotal.toFixed(2)}</span>
            </div>
            <button className="nof-place-btn" onClick={placeOrder} disabled={placing || cart.length === 0}>
              {placing
                ? <><FontAwesomeIcon icon={faSpinner} spin /> Placing Order…</>
                : <><FontAwesomeIcon icon={faCircleCheck} /> Place Order</>
              }
            </button>
            {placeError && <p className="nof-place-error">{placeError}</p>}
          </div>
        </div>
      </div>
    </div>
  );

  // ── SUCCESS STEP ─────────────────────────────────────────────
  return (
    <div className="nof-root">
      <div className="nof-success">
        <FontAwesomeIcon icon={faCircleCheck} className="nof-success-icon" />
        <h2>Order Placed!</h2>
        <p>
          {lastOrderId ? `Order #${lastOrderId} · ` : ""}
          Room {roomNumber} · EGP {cartTotal.toFixed(2)}
        </p>
        <div className="nof-success-actions">
          <button className="nof-success-new" onClick={resetFlow}>
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
            New Order
          </button>
          <button className="nof-success-view" onClick={onViewOrders}>
            <FontAwesomeIcon icon={faCartShopping} style={{ marginRight: 6 }} />
            View Orders
          </button>
        </div>
      </div>
    </div>
  );
}
